import {
  spacePublicHostnameSuffix,
  publicWildcardIsEnvironmentControlled,
} from "../../shared/publicUrl";
import { derivePublicDomain } from "../hooks/derivePublicDomain";
import { config } from "../environment";
import {
  locationTenantDomain,
  tenantApiOrigin,
  tenantPublicOrigin,
} from "./tenantOrigin";

type TrustPolicy = {
  origins: Set<string>;
  spaceOrigin?: {
    hostnameSuffix: string;
    protocol: string;
    port: string;
  };
};

function spaceOriginPolicy(
  publicOrigin: URL,
  opts: {
    publicWildcardDomain?: string;
    combinedSpaceTenantLabel?: string;
  }
): TrustPolicy["spaceOrigin"] {
  const hostnameSuffix = spacePublicHostnameSuffix(opts);
  if (!hostnameSuffix) return undefined;
  return {
    hostnameSuffix,
    protocol: publicOrigin.protocol,
    port: publicOrigin.port,
  };
}

// The origins a callbackURL may target. Multi-tenant and combined-host stacks
// derive the current tenant's hosts; single-tenant stacks trust the configured
// URLs and wildcard as-is.
function trustPolicy(): TrustPolicy {
  const origins = new Set<string>([window.location.origin]);
  if (
    !publicWildcardIsEnvironmentControlled(
      config.maxTenants,
      config.combinedSpaceTenantHosts
    ) &&
    config.stackBaseDomain
  ) {
    const tenantDomain = locationTenantDomain();
    if (!tenantDomain) return { origins };
    if (config.legacyApiSubdomain) {
      origins.add(tenantApiOrigin(config.stackBaseDomain, tenantDomain));
    }
    if (!config.stackPublicBaseDomain) return { origins };
    const publicOrigin = new URL(
      tenantPublicOrigin(config.stackPublicBaseDomain, tenantDomain)
    );
    origins.add(publicOrigin.origin);
    const { wildcardDomain, combinedSpaceTenantLabel } = derivePublicDomain(
      config.publicWildcardDomain,
      config.stackPublicBaseDomain,
      config.maxTenants,
      config.combinedSpaceTenantHosts,
      tenantDomain
    );
    if (!wildcardDomain) return { origins };
    return {
      origins,
      spaceOrigin: spaceOriginPolicy(publicOrigin, {
        publicWildcardDomain: wildcardDomain,
        combinedSpaceTenantLabel,
      }),
    };
  }
  let publicOrigin: URL | undefined;
  for (const candidate of [config.externalApiUrl, config.externalPublicUrl]) {
    if (!candidate) continue;
    try {
      const origin = new URL(candidate);
      origins.add(origin.origin);
      if (candidate === config.externalPublicUrl) publicOrigin = origin;
    } catch {}
  }
  return {
    origins,
    spaceOrigin: publicOrigin
      ? spaceOriginPolicy(publicOrigin, {
          publicWildcardDomain: config.publicWildcardDomain,
        })
      : undefined,
  };
}

/**
 * Reads the `callbackURL` query param and validates it against the set of
 * origins we trust, so a crafted link can't use the sign-in page as an
 * open redirect.
 */
export function readTrustedSignInCallbackURL(
  search: string = window.location.search
): string | null {
  const raw = new URLSearchParams(search).get("callbackURL");
  if (!raw) return null;

  let parsed: URL;
  try {
    parsed = new URL(raw, window.location.origin);
  } catch {
    return null;
  }
  if (parsed.protocol !== "https:" && parsed.protocol !== "http:") {
    return null;
  }

  const { origins, spaceOrigin } = trustPolicy();
  if (origins.has(parsed.origin)) return parsed.toString();
  if (
    spaceOrigin &&
    parsed.hostname.endsWith(spaceOrigin.hostnameSuffix) &&
    parsed.protocol === spaceOrigin.protocol &&
    parsed.port === spaceOrigin.port
  ) {
    return parsed.toString();
  }
  return null;
}
